import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
} from "reactstrap";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import Textfield from "../../../Shared/Textfield";
import Button from "../../../Shared/Button";
import { setTags } from "../../../Redux/Reducer/appReducer";
import { addTag } from "../../../../helpers/service";
import styles from "./assigntagsmodal.module.scss";
import ArrowDown from "../../../../assets/images/arrow-down-white.svg";
import ProductIcon from "../../../../assets/images/products-white-icon.svg";
import SellerIcon from "../../../../assets/images/seller-icon-white.svg";
import BrandIcon from "../../../../assets/images/brands-white-icon.svg";

const AssignTagsModal = ({
  openModal,
  toggle,
  brandsData,
  productsData,
  sellerData,
  setLoading,
}) => {
  const dispatch = useDispatch();
  const tagsData = useSelector((state) => state?.appReducer?.tags);
  const userData = useSelector((state) => state.authReducer);
  const { locations } = useSelector((state) => state?.appReducer);

  const isAdmin = userData?.role === "admin";
  const [openProductDropdown, setOpenProductDropdown] = useState(false);
  const [openBrandDropdown, setOpenBrandDropdown] = useState(false);
  const [openSellerDropdown, setOpenSellerDropdown] = useState(false);
  const [associatedProducts, setAssociatedProducts] = useState([]);
  const [brands, setBrands] = useState(brandsData);
  const [locationId, setLocationId] = useState(0);

  const formik = useFormik({
    initialValues: {
      locationId: locationId || 0,
      brandId: "",
      productId: "",
      firstUid: "",
      lastUid: "",
      sellerId: userData?.id,
      adminId: isAdmin ? userData?.id : null,
      brand: "",
      product: "",
    },
    onSubmit: handleSubmit,
  });

  const checkBrandWithSellerId = (id) => {
    const newBrands = brandsData?.filter((brand) => brand?.userId === id);

    setBrands(newBrands);
  };

  const handleLocationAdd = (brandId) => {
    const locationId =
      locations?.find((loc) => loc?.brandId === brandId)?.id || "";
    setLocationId(locationId);
  };

  const handleCheckLocation = (brandId) => {
    const relatedProducts = productsData.filter(
      (product) => product?.brandID === brandId
    );
    setAssociatedProducts(relatedProducts);

    if (!relatedProducts.length) {
      toast.warn("Selected brand has no associated products");
      return;
    }

    handleLocationAdd(brandId);
  };

  const brandSelected = Boolean(
    formik.values?.brandId && associatedProducts?.length
  );

  async function handleSubmit(values) {
    if (!values.firstUid) return toast.error("First UID is required.");
    if (!values.productId) return toast.error("Product is required.");
    if (!values.brandId) return toast.error("Brand is required.");
    if (!locationId) return toast.error("Location not found.");

    try {
      const data = {
        locationId: locationId,
        brandId: values.brandId,
        productId: values.productId,
        firstUid: values.firstUid,
        lastUid: values.lastUid,
        sellerId: values.sellerId,
        ...(isAdmin && { adminId: userData?.id }),
      };

      setLoading(true);
      const tagData = await addTag(data, userData?.idToken);

      const newTagsData = [
        ...tagsData,
        {
          ...tagData?.data,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      ];

      dispatch(setTags(newTagsData));
      toast.success("Tag assigned successfully");
      formik.resetForm();
      toggle();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      isOpen={openModal}
      toggle={toggle}
      centered
      fullscreen="sm"
      contentClassName={styles.modal_content}
      className={styles.container}
      wrapClassName={styles.modal_wrapper}
    >
      <div className={styles.managetags_modal_container}>
        <div>
          <p className={styles.header_text}>Assign Tags</p>
        </div>
        <div>
          <p className={styles.label_text}>
            Assign NFC tags to products so the correct product information
            appears when a user taps their phone on the tag in the LRX app. You
            can find the UID of a tag by scanning it in the LRX app.
          </p>
        </div>
        <div className={styles.dropdowns_container}>
          {isAdmin && (
            <Dropdown
              isOpen={openSellerDropdown}
              toggle={() => setOpenSellerDropdown(!openSellerDropdown)}
            >
              <DropdownToggle className={styles.dropdown_toggle}>
                <img src={SellerIcon} alt="seller" width={24} height={24} />
                <p className={styles.filterLabel}>
                  {formik.values?.seller || "Sellers"}
                </p>
                <img src={ArrowDown} alt="icon" width={32} height={32} />
              </DropdownToggle>
              <DropdownMenu className={styles.dropdown_menu}>
                {sellerData?.map((seller) => (
                  <DropdownItem
                    key={seller.id}
                    onClick={() => {
                      checkBrandWithSellerId(seller.id);
                      formik.setValues({
                        ...formik.values,
                        seller: seller.name,
                        sellerId: seller.id,
                      });
                    }}
                  >
                    {seller.name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          )}

          <Dropdown
            isOpen={openBrandDropdown}
            toggle={() => setOpenBrandDropdown(!openBrandDropdown)}
          >
            <DropdownToggle className={styles.dropdown_toggle}>
              <img src={BrandIcon} alt="brand" width={24} height={24} />
              <p className={styles.filterLabel}>
                {formik.values?.brand || "Brands"}
              </p>
              <img src={ArrowDown} alt="icon" width={32} height={32} />
            </DropdownToggle>
            <DropdownMenu className={styles.dropdown_menu}>
              <div style={{ padding: "0 15px", fontWeight: "bold" }}>
                {brands?.length === 0 ? "No Brand" : "Select Brand"}
              </div>
              {brands?.map((brand) => (
                <DropdownItem
                  key={brand.id}
                  onClick={() => {
                    handleCheckLocation(brand.id);
                    formik.setValues({
                      ...formik.values,
                      brand: brand.brandName,
                      brandId: brand.id,
                    });
                  }}
                >
                  {brand.brandName}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>

          <Dropdown
            isOpen={openProductDropdown}
            toggle={() =>
              brandSelected && setOpenProductDropdown(!openProductDropdown)
            }
          >
            <DropdownToggle
              className={`${styles.dropdown_toggle} ${
                brandSelected ? "" : styles.disabled_dropdown
              }`}
            >
              <img src={ProductIcon} alt="product" width={24} height={24} />
              <p className={styles.filterLabel}>
                {formik.values?.product || "Products"}
              </p>
              <img src={ArrowDown} alt="icon" width={32} height={32} />
            </DropdownToggle>
            <DropdownMenu className={styles.dropdown_menu}>
              {associatedProducts?.map((product) => (
                <DropdownItem
                  key={product.id}
                  onClick={() => {
                    formik.setValues({
                      ...formik.values,
                      product: product.name,
                      productId: product.id,
                    });
                  }}
                >
                  {product.name || "-"}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
        <Textfield
          placeholder="First UID"
          className={styles.input_field_container}
          disabled={!brandSelected}
          name="firstUid"
          id="firstUid"
          onChange={formik.handleChange}
          error={formik.errors.firstUid}
          touched={formik.touched.firstUid}
        />
        <Textfield
          placeholder="Last UID"
          className={styles.input_field_container}
          disabled={!brandSelected}
          name="lastUid"
          id="lastUid"
          onChange={formik.handleChange}
          error={formik.errors.lastUid}
          touched={formik.touched.lastUid}
          style={{ marginTop: "12px" }}
        />
        <p className={styles.label_footer_text}>
          We&apos;ll assign the product to both NFC tags and every tag in
          between them on the same roll.
        </p>
        <div className={styles.button_container}>
          <Button
            className={styles.button_enable}
            onClick={formik.submitForm}
            text="Assign"
          />
        </div>
      </div>
    </Modal>
  );
};

AssignTagsModal.propTypes = {
  openModal: PropTypes.bool,
  toggle: PropTypes.func,
  brandsData: PropTypes.array,
  productsData: PropTypes.array,
  sellerData: PropTypes.array,
  setLoading: PropTypes.func,
};

export default AssignTagsModal;
